import React, { useState, useEffect } from 'react';
import { Layout, Menu, Card, Table, Button, Modal, Form, Input, Select, InputNumber, message, Space, Tag, Statistic, Row, Col, Switch, Typography, Popconfirm } from 'antd';
// 管理员推荐和积分管理功能 - v2.0
import {
  UserOutlined,
  SettingOutlined,
  BgColorsOutlined,
  KeyOutlined,
  DashboardOutlined,
  ArrowLeftOutlined,
  DollarOutlined,
  BgColorsOutlined as PaletteOutlined,
  DatabaseOutlined,
  DownloadOutlined,
  UploadOutlined,
  CloudDownloadOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  TeamOutlined,
  CrownOutlined
} from '@ant-design/icons';
import TsAgentAdminPanel from './admin/TsAgentAdminPanel';
import AdminManagePanel from './admin/AdminManagePanel';
import SystemSettingsPanel from './admin/SystemSettingsPanel';
import axios from 'axios';
import { useAuthStore } from '../stores/authStore';
import useThemeStore from '../stores/themeStore';

// 配置axios默认URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const Admin = () => {
  const { user, logout } = useAuthStore();
  const { refreshTheme } = useThemeStore();
  const [selectedMenu, setSelectedMenu] = useState('dashboard');
  const [users, setUsers] = useState([]);
  const [themes, setThemes] = useState([]);
  const [activationCodes, setActivationCodes] = useState([]);
  const [activationCodesPagination, setActivationCodesPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [cardPrices, setCardPrices] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [userSearchText, setUserSearchText] = useState('');
  
  // 推荐和积分管理相关状态
  const [referralModalVisible, setReferralModalVisible] = useState(false);
  const [pointsModalVisible, setPointsModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [pointsData, setPointsData] = useState(null);
  const [pointsForm] = Form.useForm();
  
  // 数据库备份相关状态
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [backupHistory, setBackupHistory] = useState([]);
  const [showBackupModal, setShowBackupModal] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      logout();
      return;
    }
    fetchData();
  }, [selectedMenu]);

  // 监听分页变化，重新获取激活码数据
  useEffect(() => {
    if (selectedMenu === 'activation-codes' && user?.role === 'admin') {
      fetchData();
    }
  }, [activationCodesPagination.current, activationCodesPagination.pageSize]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 确保每次请求都包含认证token
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      const userRole = localStorage.getItem('userRole');
      
      console.log('Admin.js - 获取的认证信息:', {
        token: token ? '存在' : '不存在',
        userId: userId,
        userRole: userRole
      });
      
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        axios.defaults.headers.common['User-ID'] = userId;
        axios.defaults.headers.common['User-Role'] = userRole;
      }

      switch (selectedMenu) {
        case 'dashboard':
          const statsResponse = await axios.get('/admin/stats', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'user-id': userId,
              'user-role': userRole
            }
          });
          if (statsResponse.data.success) {
            setStats(statsResponse.data.stats);
          }
          break;
        case 'users':
          const usersResponse = await axios.get('/admin/users', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'user-id': userId,
              'user-role': userRole
            }
          });
          if (usersResponse.data.success) {
            setUsers(usersResponse.data.users);
          }
          break;
        case 'themes':
          const themesResponse = await axios.get('/admin/themes', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'user-id': userId,
              'user-role': userRole
            }
          });
          if (themesResponse.data.success) {
            setThemes(themesResponse.data.themes);
          }
          break;
        case 'activation-codes':
          const codesResponse = await axios.get('/admin/activation-codes', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'user-id': userId,
              'user-role': userRole
            },
            params: {
              page: activationCodesPagination.current,
              limit: activationCodesPagination.pageSize
            }
          });
          if (codesResponse.data.success) {
            setActivationCodes(codesResponse.data.codes);
            setActivationCodesPagination({
              current: codesResponse.data.pagination.page,
              pageSize: codesResponse.data.pagination.limit,
              total: codesResponse.data.pagination.total
            });
          }
          break;
        case 'card-prices':
          const pricesResponse = await axios.get('/admin/card-prices', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'user-id': userId,
              'user-role': userRole
            }
          });
          if (pricesResponse.data.success) {
            setCardPrices(pricesResponse.data.cardPrices);
          }
          break;
        case 'database-backup':
          fetchBackupHistory();
          break;
      }
    } catch (error) {
      console.error('获取数据失败:', error);
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (key) => {
    setSelectedMenu(key);
  };

  const handleBackToApp = () => {
    window.location.href = '/';
  };

  // 数据库备份相关函数
  const handleCreateBackup = async () => {
    setBackupLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      const userRole = localStorage.getItem('userRole');
      
      const response = await axios.post('/admin/database/backup', {
        description: `管理员备份 - ${new Date().toLocaleString()}`
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'user-id': userId,
          'user-role': userRole
        }
      });
      
      if (response.data.success) {
        message.success('数据库备份创建成功');
        fetchBackupHistory();
      } else {
        message.error(response.data.message || '备份创建失败');
      }
    } catch (error) {
      console.error('创建备份失败:', error);
      message.error('备份创建失败');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreBackup = async (backupId) => {
    setRestoreLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      const userRole = localStorage.getItem('userRole');
      
      const response = await axios.post(`/admin/database/restore/${backupId}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'user-id': userId,
          'user-role': userRole
        }
      });
      
      if (response.data.success) {
        message.success('数据库还原成功');
        fetchBackupHistory();
      } else {
        message.error(response.data.message || '还原失败');
      }
    } catch (error) {
      console.error('还原备份失败:', error);
      message.error('还原失败');
    } finally {
      setRestoreLoading(false);
    }
  };

  const fetchBackupHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      const userRole = localStorage.getItem('userRole');
      
      const response = await axios.get('/admin/database/backups', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'user-id': userId,
          'user-role': userRole
        }
      });
      
      if (response.data.success) {
        setBackupHistory(response.data.backups || []);
      }
    } catch (error) {
      console.error('获取备份历史失败:', error);
    }
  };

  const handleDownloadBackup = async (backupId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      const userRole = localStorage.getItem('userRole');
      
      const response = await axios.get(`/admin/database/download/${backupId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'user-id': userId,
          'user-role': userRole
        },
        responseType: 'blob'
      });
      
      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      message.success('备份文件下载成功');
    } catch (error) {
      console.error('下载备份失败:', error);
      message.error('下载失败');
    }
  };

  const handleDeleteBackup = async (backupId) => {
    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      const userRole = localStorage.getItem('userRole');
      
      const response = await axios.delete(`/admin/database/backup/${backupId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'user-id': userId,
          'user-role': userRole
        }
      });
      
      if (response.data.success) {
        message.success('备份删除成功');
        fetchBackupHistory(); // 刷新备份列表
      } else {
        message.error(response.data.message || '删除备份失败');
      }
    } catch (error) {
      console.error('删除备份失败:', error);
      message.error('删除备份失败');
    }
  };

  // 根据主题名称获取不同的图标
  const getThemeIcon = (themeName, primaryColor) => {
    const iconMap = {
      '默认': <BgColorsOutlined style={{ color: primaryColor, fontSize: '24px' }} />,
      '蓝色': <BgColorsOutlined style={{ color: primaryColor, fontSize: '24px' }} />,
      '绿色': <BgColorsOutlined style={{ color: primaryColor, fontSize: '24px' }} />,
      '红色': <BgColorsOutlined style={{ color: primaryColor, fontSize: '24px' }} />,
      '紫色': <BgColorsOutlined style={{ color: primaryColor, fontSize: '24px' }} />,
      '橙色': <BgColorsOutlined style={{ color: primaryColor, fontSize: '24px' }} />,
      '粉色': <BgColorsOutlined style={{ color: primaryColor, fontSize: '24px' }} />,
      '深色': <BgColorsOutlined style={{ color: primaryColor, fontSize: '24px' }} />,
      '浅色': <BgColorsOutlined style={{ color: primaryColor, fontSize: '24px' }} />,
    };
    
    return iconMap[themeName] || <PaletteOutlined style={{ color: primaryColor, fontSize: '24px' }} />;
  };

  const renderDashboard = () => (
    <div>
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              border: 'none',
              transition: 'all 0.3s ease'
            }}
            bodyStyle={{ padding: '6px' }}
          >
            <Statistic
              title="总用户数"
              value={stats.totalUsers || 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              border: 'none',
              transition: 'all 0.3s ease'
            }}
            bodyStyle={{ padding: '6px' }}
          >
            <Statistic
              title="管理员数"
              value={stats.totalAdmins || 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              border: 'none',
              transition: 'all 0.3s ease'
            }}
            bodyStyle={{ padding: '6px' }}
          >
            <Statistic
              title="今日注册"
              value={stats.todayUsers || 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              border: 'none',
              transition: 'all 0.3s ease'
            }}
            bodyStyle={{ padding: '6px' }}
          >
            <Statistic
              title="可用卡密"
              value={stats.availableCodes || 0}
              prefix={<KeyOutlined />}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );

  const renderUsers = () => {
    const filteredUsers = users.filter(user => 
      user.username?.toLowerCase().includes(userSearchText.toLowerCase()) ||
      user.phone?.includes(userSearchText) ||
      user.email?.toLowerCase().includes(userSearchText.toLowerCase()) ||
      user.nickname?.toLowerCase().includes(userSearchText.toLowerCase())
    );

    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
      },
      {
        title: '手机号',
        dataIndex: 'phone',
        key: 'phone',
        width: 120,
      },
      {
        title: '昵称',
        dataIndex: 'nickname',
        key: 'nickname',
        width: 120,
      },
      {
        title: '角色',
        dataIndex: 'role',
        key: 'role',
        width: 100,
        render: (role) => (
          <Tag color={role === 'admin' ? 'red' : 'blue'}>
            {role === 'admin' ? '管理员' : '用户'}
          </Tag>
        ),
      },
      {
        title: '会员级别',
        dataIndex: 'member_level',
        key: 'member_level',
        width: 120,
        render: (level) => {
          const colors = {
            '普通会员': 'default',
            '高级会员': 'green',
            '银卡会员': 'blue',
            '金卡会员': 'orange',
            '钻石会员': 'purple',
            'VIP会员': 'red'
          };
          return <Tag color={colors[level] || 'default'}>{level}</Tag>;
        },
      },
      {
        title: '备注',
        dataIndex: 'remark',
        key: 'remark',
        ellipsis: true,
      },
      {
        title: '注册时间',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 150,
        render: (date) => new Date(date).toLocaleString('zh-CN'),
      },
      {
        title: '操作',
        key: 'action',
        width: 200,
        render: (_, record) => (
          <Space>
            <Button
              type="link"
              size="small"
              onClick={() => handleResetPassword(record.id)}
            >
              重置密码
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => handleSetMemberLevel(record)}
            >
              设置级别
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => handleSetRole(record)}
            >
              设置角色
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => handleViewReferrals(record)}
            >
              查看推荐
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => handleManagePoints(record)}
            >
              积分管理
            </Button>
            {record.id !== user?.id && (
              <Button
                type="link"
                danger
                size="small"
                onClick={() => handleDeleteUser(record.id)}
              >
                删除
              </Button>
            )}
          </Space>
        ),
      },
    ];

    return (
      <Card 
        title="用户管理"
        extra={
          <Input.Search
            placeholder="搜索用户名、手机号、邮箱或昵称"
            value={userSearchText}
            onChange={(e) => setUserSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
        }
        style={{ 
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
          border: 'none',
          transition: 'all 0.3s ease'
        }}
        bodyStyle={{ padding: '6px' }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text>加载中...</Text>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <UserOutlined style={{ fontSize: '48px', color: '#D9D9D9', marginBottom: '16px' }} />
            <Title level={4} style={{ color: '#D9D9D9' }}>
              {userSearchText ? '未找到匹配的用户' : '暂无用户'}
            </Title>
            <Text type="secondary">
              {userSearchText ? '请尝试其他搜索关键词' : '系统中还没有用户'}
            </Text>
          </div>
        ) : (
          <Row gutter={[16, 16]}>
            {filteredUsers.map((user) => (
              <Col xs={24} sm={12} md={8} lg={6} key={user.id}>
                <Card
                  hoverable
                  style={{ 
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  bodyStyle={{ padding: '6px' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                  }}
                  actions={[
                    <Button
                      type="text"
                      size="small"
                      onClick={() => handleResetPassword(user.id)}
                      key="reset"
                    >
                      重置
                    </Button>,
                    <Button
                      type="text"
                      size="small"
                      onClick={() => handleSetMemberLevel(user)}
                      key="level"
                    >
                      级别
                    </Button>,
                    <Button
                      type="text"
                      size="small"
                      onClick={() => handleSetRole(user)}
                      key="role"
                    >
                      角色
                    </Button>,
                    <Button
                      type="text"
                      size="small"
                      onClick={() => handleViewReferrals(user)}
                      key="referral"
                    >
                      推荐
                    </Button>,
                    <Button
                      type="text"
                      size="small"
                      onClick={() => handleManagePoints(user)}
                      key="points"
                    >
                      积分
                    </Button>,
                    user.id !== user?.id && (
                      <Button
                        type="text"
                        danger
                        size="small"
                        onClick={() => handleDeleteUser(user.id)}
                        key="delete"
                      >
                        删除
                      </Button>
                    )
                  ].filter(Boolean)}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                      👤
                    </div>
                    <Title level={5} style={{ margin: '8px 0', fontSize: '14px' }}>
                      {user.username || user.nickname || '未设置'}
                    </Title>
                    <div style={{ marginBottom: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        📱 {user.phone || '未设置'}
                      </Text>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        📧 {user.email || '未设置'}
                      </Text>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <Tag color={user.role === 'admin' ? 'red' : 'blue'} style={{ fontSize: '10px' }}>
                        {user.role === 'admin' ? '管理员' : '用户'}
                      </Tag>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '10px' }}>
                        📅 {new Date(user.created_at).toLocaleDateString('zh-CN')}
                      </Text>
                    </div>
                    {user.member_level && (
                      <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '8px' }}>
                        🏆 {user.member_level}
                      </Text>
                    )}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>
    );
  };

  const renderThemes = () => {
    const columns = [
      {
        title: '主题名称',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: '主色调',
        dataIndex: 'primary_color',
        key: 'primary_color',
        render: (color) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                width: '20px',
                height: '20px',
                backgroundColor: color,
                marginRight: '8px',
                borderRadius: '4px'
              }}
            />
            {color}
          </div>
        )
      },
      {
        title: '状态',
        dataIndex: 'is_active',
        key: 'is_active',
        render: (active) => (
          <Tag color={active ? 'green' : 'default'}>
            {active ? '激活' : '未激活'}
          </Tag>
        )
      },
      {
        title: '操作',
        key: 'action',
        render: (_, record) => (
          <Space>
            <Button
              type="link"
              onClick={() => handleEditTheme(record)}
            >
              编辑
            </Button>
            {!record.is_active && (
              <Button
                type="link"
                onClick={() => handleActivateTheme(record.id)}
              >
                激活
              </Button>
            )}
            {!record.is_active && (
              <Button
                type="link"
                danger
                onClick={() => handleDeleteTheme(record.id)}
              >
                删除
              </Button>
            )}
          </Space>
        )
      }
    ];

    return (
      <Card
        title="主题管理"
        extra={
          <Button
            type="primary"
            onClick={() => {
              setModalType('theme');
              setEditingItem(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            添加主题
          </Button>
        }
        style={{ 
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
          border: 'none',
          transition: 'all 0.3s ease'
        }}
        bodyStyle={{ padding: '6px' }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text>加载中...</Text>
          </div>
        ) : themes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <PaletteOutlined style={{ fontSize: '48px', color: '#D9D9D9', marginBottom: '16px' }} />
            <Title level={4} style={{ color: '#D9D9D9' }}>暂无主题</Title>
            <Text type="secondary">点击"添加主题"开始管理主题</Text>
          </div>
        ) : (
          <Row gutter={[16, 16]}>
            {themes.map((theme) => (
              <Col xs={24} sm={12} md={8} lg={6} key={theme.id}>
                <Card
                  hoverable
                  style={{ 
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    background: `linear-gradient(135deg, ${theme.primary_color}10, ${theme.primary_color}05, #fff)`,
                    border: `1px solid ${theme.primary_color}20`,
                    transition: 'all 0.3s ease'
                  }}
                  bodyStyle={{ padding: '6px' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                    e.currentTarget.style.boxShadow = `0 8px 30px ${theme.primary_color}30`;
                    e.currentTarget.style.background = `linear-gradient(135deg, ${theme.primary_color}20, ${theme.primary_color}10, #fff)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.background = `linear-gradient(135deg, ${theme.primary_color}10, ${theme.primary_color}05, #fff)`;
                  }}
                  actions={[
                    <Button
                      type="text"
                      onClick={() => handleEditTheme(theme)}
                      size="small"
                      key="edit"
                    >
                      编辑
                    </Button>,
                    !theme.is_active && (
                      <Button
                        type="text"
                        onClick={() => handleActivateTheme(theme.id)}
                        size="small"
                        key="activate"
                      >
                        激活
                      </Button>
                    ),
                    !theme.is_active && (
                      <Button
                        type="text"
                        danger
                        onClick={() => handleDeleteTheme(theme.id)}
                        size="small"
                        key="delete"
                      >
                        删除
                      </Button>
                    )
                  ].filter(Boolean)}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '32px', 
                      marginBottom: '8px',
                      background: `linear-gradient(135deg, ${theme.primary_color}20, ${theme.primary_color}40)`,
                      borderRadius: '50%',
                      width: '60px',
                      height: '60px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 8px auto',
                      border: `2px solid ${theme.primary_color}30`
                    }}>
                      {getThemeIcon(theme.name, theme.primary_color)}
                    </div>
                    <Title level={5} style={{ margin: '8px 0', fontSize: '14px' }}>
                      {theme.name}
                    </Title>
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            backgroundColor: theme.primary_color,
                            borderRadius: '4px',
                            border: `1px solid ${theme.primary_color}30`
                          }}
                        />
                        <Text type="secondary" style={{ fontSize: '10px' }}>
                          {theme.primary_color}
                        </Text>
                      </div>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <Tag color={theme.is_active ? 'green' : 'default'} style={{ fontSize: '10px' }}>
                        {theme.is_active ? '激活' : '未激活'}
                      </Tag>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>
    );
  };

  const renderActivationCodes = () => {
    const columns = [
      {
        title: '卡密',
        dataIndex: 'code',
        key: 'code',
      },
      {
        title: '类型',
        dataIndex: 'type',
        key: 'type',
        render: (type) => <Tag color="blue">{type}</Tag>
      },
      {
        title: '有效期(天)',
        dataIndex: 'duration_days',
        key: 'duration_days',
      },
      {
        title: '状态',
        dataIndex: 'is_used',
        key: 'is_used',
        render: (used) => (
          <Tag color={used ? 'red' : 'green'}>
            {used ? '已使用' : '未使用'}
          </Tag>
        )
      },
      {
        title: '使用者',
        dataIndex: 'used_by_username',
        key: 'used_by_username',
        render: (username) => username || '-'
      },
      {
        title: '操作',
        key: 'action',
        render: (_, record) => (
          <Space>
            {!record.is_used && (
              <Button
                type="link"
                danger
                onClick={() => handleDeleteCode(record.id)}
              >
                删除
              </Button>
            )}
          </Space>
        )
      }
    ];

    return (
      <Card
        title="卡密管理"
        extra={
          <Button
            type="primary"
            onClick={() => {
              setModalType('code');
              setEditingItem(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            生成卡密
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={activationCodes}
          loading={loading}
          rowKey="id"
          pagination={{
            current: activationCodesPagination.current,
            pageSize: activationCodesPagination.pageSize,
            total: activationCodesPagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, pageSize) => {
              setActivationCodesPagination(prev => ({
                ...prev,
                current: page,
                pageSize: pageSize || prev.pageSize
              }));
            }
          }}
        />
      </Card>
    );
  };

  const renderCardPrices = () => {
    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
      },
      {
        title: '卡类名称',
        dataIndex: 'card_type',
        key: 'card_type',
        width: 120,
      },
      {
        title: '价格 (元)',
        dataIndex: 'price',
        key: 'price',
        width: 100,
        render: (price) => `¥${price}`
      },
      {
        title: '描述',
        dataIndex: 'description',
        key: 'description',
        ellipsis: true,
      },
      {
        title: '原价 (元)',
        dataIndex: 'original_price',
        key: 'original_price',
        width: 100,
        render: (price) => `¥${price}`
      },
      {
        title: '状态',
        dataIndex: 'is_active',
        key: 'is_active',
        width: 120,
        render: (active, record) => (
          <Space>
            <Tag color={active ? 'green' : 'default'}>
              {active ? '启用' : '禁用'}
            </Tag>
            <Switch
              size="small"
              checked={!!active}
              onChange={async (checked) => {
                try {
                  const token = localStorage.getItem('token');
                  const userId = localStorage.getItem('userId');
                  const userRole = localStorage.getItem('userRole');
                  const headers = { 'Authorization': `Bearer ${token}`, 'user-id': userId, 'user-role': userRole };
                  const payload = {
                    price: record.price,
                    original_price: record.original_price,
                    description: record.description,
                    is_active: checked ? 1 : 0,
                    sort_order: record.sort_order ?? 0
                  };
                  const resp = await axios.put(`/admin/card-prices/${record.id}`, payload, { headers });
                  if (resp.data.success) {
                    message.success(`已${checked ? '启用' : '禁用'}`);
                    fetchData();
                  }
                } catch (e) {
                  message.error('更新状态失败');
                }
              }}
            />
          </Space>
        )
      },
      {
        title: '操作',
        key: 'action',
        width: 150,
        render: (_, record) => (
          <Space>
            <Button
              type="link"
              size="small"
              onClick={() => handleEditCardPrice(record)}
            >
              编辑
            </Button>
            <Button
              type="link"
              danger
              size="small"
              onClick={() => handleDeleteCardPrice(record.id)}
            >
              删除
            </Button>
          </Space>
        )
      }
    ];

    return (
      <Card
        title="卡类定价管理"
        extra={
          <Button
            type="primary"
            onClick={() => {
              setModalType('card-price');
              setEditingItem(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            添加新定价
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={cardPrices}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </Card>
    );
  };

  const renderDatabaseBackup = () => {
    const backupColumns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
      },
      {
        title: '文件名',
        dataIndex: 'filename',
        key: 'filename',
        ellipsis: true,
      },
      {
        title: '描述',
        dataIndex: 'description',
        key: 'description',
        ellipsis: true,
      },
      {
        title: '文件大小',
        dataIndex: 'fileSize',
        key: 'fileSize',
        width: 120,
        render: (size) => {
          if (!size || size === 0) return '0 B';
          if (size < 1024) return `${size} B`;
          if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
          return `${(size / (1024 * 1024)).toFixed(1)} MB`;
        }
      },
      {
        title: '创建时间',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 180,
        render: (date) => {
          if (!date) return '未知';
          try {
            return new Date(date).toLocaleString('zh-CN');
          } catch (error) {
            return '格式错误';
          }
        }
      },
      {
        title: '操作',
        key: 'action',
        width: 200,
        render: (_, record) => (
          <Space>
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadBackup(record.id, record.filename)}
            >
              下载
            </Button>
            <Button
              type="link"
              size="small"
              icon={<CloudUploadOutlined />}
              loading={restoreLoading}
              onClick={() => handleRestoreBackup(record.id)}
              style={{ color: '#ff4d4f' }}
            >
              还原
            </Button>
            <Popconfirm
              title="确定要删除这个备份吗？"
              description="删除后将无法恢复，请谨慎操作。"
              onConfirm={() => handleDeleteBackup(record.id)}
              okText="确定删除"
              cancelText="取消"
            >
              <Button
                type="link"
                size="small"
                icon={<DeleteOutlined />}
                danger
              >
                删除
              </Button>
            </Popconfirm>
          </Space>
        )
      }
    ];

    return (
      <div>
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={8}>
            <Card
              style={{ 
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                border: 'none',
                transition: 'all 0.3s ease'
              }}
              bodyStyle={{ padding: '24px', textAlign: 'center' }}
            >
              <DatabaseOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
              <Title level={4} style={{ margin: 0, marginBottom: '8px' }}>
                数据库备份
              </Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                创建当前数据库的完整备份
              </Text>
              <Button
                type="primary"
                icon={<CloudDownloadOutlined />}
                loading={backupLoading}
                onClick={handleCreateBackup}
                size="large"
                style={{ width: '100%' }}
              >
                创建备份
              </Button>
            </Card>
          </Col>
          <Col span={8}>
            <Card
              style={{ 
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                border: 'none',
                transition: 'all 0.3s ease'
              }}
              bodyStyle={{ padding: '24px', textAlign: 'center' }}
            >
              <CloudUploadOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
              <Title level={4} style={{ margin: 0, marginBottom: '8px' }}>
                数据库还原
              </Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                从备份文件还原数据库
              </Text>
              <Button
                type="default"
                icon={<UploadOutlined />}
                onClick={() => setShowBackupModal(true)}
                size="large"
                style={{ width: '100%' }}
              >
                选择备份
              </Button>
            </Card>
          </Col>
          <Col span={8}>
            <Card
              style={{ 
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                border: 'none',
                transition: 'all 0.3s ease'
              }}
              bodyStyle={{ padding: '24px', textAlign: 'center' }}
            >
              <DownloadOutlined style={{ fontSize: '48px', color: '#faad14', marginBottom: '16px' }} />
              <Title level={4} style={{ margin: 0, marginBottom: '8px' }}>
                备份历史
              </Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                查看和管理所有备份文件
              </Text>
              <Button
                type="default"
                icon={<DatabaseOutlined />}
                onClick={fetchBackupHistory}
                size="large"
                style={{ width: '100%' }}
              >
                刷新列表
              </Button>
            </Card>
          </Col>
        </Row>

        <Card title="备份历史记录" style={{ borderRadius: '16px' }}>
          <Table
            columns={backupColumns}
            dataSource={backupHistory}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            }}
          />
        </Card>

        {/* 备份还原确认弹窗 */}
        {showBackupModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              width: '600px',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                paddingBottom: '12px',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
                  选择备份文件进行还原
                </h3>
                <Button 
                  type="text" 
                  onClick={() => setShowBackupModal(false)}
                  style={{ fontSize: '18px', width: '24px', height: '24px' }}
                >
                  ×
                </Button>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <CloudUploadOutlined style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: '16px', display: 'block', textAlign: 'center' }} />
                <Title level={5} style={{ color: '#ff4d4f', textAlign: 'center', marginBottom: '16px' }}>
                  警告：数据库还原操作
                </Title>
                <div style={{ marginBottom: '20px', textAlign: 'left', padding: '16px', backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: '6px' }}>
                  <Text strong>请注意：</Text>
                  <ul style={{ marginTop: '10px', paddingLeft: '20px', marginBottom: 0 }}>
                    <li>还原操作将<Text strong style={{ color: '#ff4d4f' }}>完全替换</Text>当前数据库</li>
                    <li>当前所有数据将被<Text strong style={{ color: '#ff4d4f' }}>永久删除</Text></li>
                    <li>请确保已备份重要数据</li>
                    <li>还原过程可能需要几分钟时间</li>
                    <li>还原期间系统将暂时不可用</li>
                  </ul>
                </div>
              </div>

              <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: '6px' }}>
                {backupHistory.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    暂无备份文件
                  </div>
                ) : (
                  backupHistory.map((backup) => (
                    <div
                      key={backup.id}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                          {backup.filename}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {backup.description} • {new Date(backup.created_at).toLocaleString('zh-CN')}
                        </div>
                      </div>
                      <Button
                        type="primary"
                        danger
                        size="small"
                        loading={restoreLoading}
                        onClick={() => {
                          setShowBackupModal(false);
                          handleRestoreBackup(backup.id);
                        }}
                      >
                        还原
                      </Button>
                    </div>
                  ))
                )}
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                marginTop: '20px',
                paddingTop: '12px',
                borderTop: '1px solid #f0f0f0'
              }}>
                <Button onClick={() => setShowBackupModal(false)}>
                  取消
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleEditUser = (user) => {
    setModalType('user');
    setEditingItem(user);
    form.setFieldsValue(user);
    setModalVisible(true);
  };

  const handleDeleteUser = async (id) => {
    try {
      const response = await axios.delete(`/admin/users/${id}`);
      if (response.data.success) {
        message.success('用户删除成功');
        fetchData();
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleResetPassword = async (id) => {
    try {
      const response = await axios.put(`/admin/users/${id}/reset-password`);
      if (response.data.success) {
        message.success(`密码重置成功！新密码是：${response.data.newPassword}`);
        fetchData();
      }
    } catch (error) {
      message.error('密码重置失败');
    }
  };

  const handleSetMemberLevel = (user) => {
    setModalType('member-level');
    setEditingItem(user);
    form.setFieldsValue({ member_level: user.member_level });
    setModalVisible(true);
  };

  const handleSetRole = (user) => {
    setModalType('role');
    setEditingItem(user);
    form.setFieldsValue({ role: user.role });
    setModalVisible(true);
  };

  // 查看推荐
  const handleViewReferrals = async (user) => {
    try {
      setSelectedUser(user);
      setLoading(true);
      const response = await axios.get(`/admin/users/${user.id}/referrals`);
      if (response.data.success) {
        setReferralData(response.data.data);
        setReferralModalVisible(true);
      }
    } catch (error) {
      message.error('获取推荐数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 积分管理
  const handleManagePoints = async (user) => {
    try {
      setSelectedUser(user);
      setLoading(true);
      const response = await axios.get(`/admin/users/${user.id}/points`);
      if (response.data.success) {
        setPointsData(response.data.data);
        pointsForm.setFieldsValue({
          used_points: response.data.data.usedPoints || 0
        });
        setPointsModalVisible(true);
      }
    } catch (error) {
      message.error('获取积分数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 更新积分
  const handleUpdatePoints = async (values) => {
    try {
      const response = await axios.put(`/admin/users/${selectedUser.id}/points`, {
        used_points: values.used_points
      });
      if (response.data.success) {
        message.success('积分更新成功');
        setPointsModalVisible(false);
        // 重新获取数据
        handleManagePoints(selectedUser);
      }
    } catch (error) {
      message.error('更新积分失败');
    }
  };

  // 分享推荐列表
  const handleShareReferrals = () => {
    const text = `【${selectedUser.nickname || selectedUser.phone}的推荐记录】
总推荐人数：${referralData.totalReferrals}
今日推荐：${referralData.todayReferrals}
VIP推荐：${referralData.vipReferrals}

推荐列表：
${referralData.referralList.map((item, index) => 
  `${index + 1}. ${item.nickname} (${item.phone}) - ${item.statusText} - ${item.joinDate}`
).join('\n')}`;
    
    navigator.clipboard.writeText(text);
    message.success('推荐记录已复制到剪贴板');
  };

  // 分享积分记录
  const handleSharePoints = () => {
    const text = `【${selectedUser.nickname || selectedUser.phone}的积分记录】
总积分：${pointsData.totalPoints}
已用积分：${pointsData.usedPoints}
剩余积分：${pointsData.remainingPoints}

近期积分记录：
${pointsData.recentLogs.map((log, index) => 
  `${index + 1}. ${log.createdAt} - ${log.description} - ${log.points > 0 ? '+' : ''}${log.points}`
).join('\n')}`;
    
    navigator.clipboard.writeText(text);
    message.success('积分记录已复制到剪贴板');
  };

  const handleEditTheme = (theme) => {
    setModalType('theme');
    setEditingItem(theme);
    form.setFieldsValue(theme);
    setModalVisible(true);
  };

  const handleActivateTheme = async (id) => {
    try {
      const response = await axios.put(`/admin/themes/${id}/activate`);
      if (response.data.success) {
        message.success('主题激活成功');
        fetchData();
        // 刷新主题
        refreshTheme();
      }
    } catch (error) {
      message.error('激活失败');
    }
  };

  const handleDeleteTheme = async (id) => {
    try {
      const response = await axios.delete(`/admin/themes/${id}`);
      if (response.data.success) {
        message.success('主题删除成功');
        fetchData();
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleDeleteCode = async (id) => {
    try {
      const response = await axios.delete(`/admin/activation-codes/${id}`);
      if (response.data.success) {
        message.success('卡密删除成功');
        fetchData();
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleEditCardPrice = (cardPrice) => {
    setModalType('card-price');
    setEditingItem(cardPrice);
    form.setFieldsValue(cardPrice);
    setModalVisible(true);
  };

  const handleDeleteCardPrice = async (id) => {
    try {
      const response = await axios.delete(`/admin/card-prices/${id}`);
      if (response.data.success) {
        message.success('卡类定价删除成功');
        fetchData();
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      switch (modalType) {
        case 'user':
          const userResponse = await axios.put(`/admin/users/${editingItem.id}/role`, values);
          if (userResponse.data.success) {
            message.success('用户角色更新成功');
          }
          break;
        case 'role':
          const roleResponse = await axios.put(`/admin/users/${editingItem.id}/role`, values);
          if (roleResponse.data.success) {
            message.success('用户角色设置成功');
          }
          break;
        case 'member-level':
          const levelResponse = await axios.put(`/admin/users/${editingItem.id}/member-level`, values);
          if (levelResponse.data.success) {
            message.success('会员级别设置成功');
          }
          break;
        case 'theme':
          if (editingItem) {
            const themeResponse = await axios.put(`/admin/themes/${editingItem.id}`, values);
            if (themeResponse.data.success) {
              message.success('主题更新成功');
            }
          } else {
            const themeResponse = await axios.post('/admin/themes', values);
            if (themeResponse.data.success) {
              message.success('主题创建成功');
            }
          }
          break;
        case 'code':
          const codeResponse = await axios.post('/admin/activation-codes', values);
          if (codeResponse.data.success) {
            message.success('卡密生成成功');
          }
          break;
        case 'card-price':
          if (editingItem) {
            const cardPriceResponse = await axios.put(`/admin/card-prices/${editingItem.id}`, values);
            if (cardPriceResponse.data.success) {
              message.success('卡类定价更新成功');
            }
          } else {
            const cardPriceResponse = await axios.post('/admin/card-prices', values);
            if (cardPriceResponse.data.success) {
              message.success('卡类定价创建成功');
            }
          }
          break;
      }
      
      fetchData();
      setModalVisible(false);
      setEditingItem(null);
      form.resetFields();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const renderModal = () => {
    let title = '';
    let formItems = [];

    switch (modalType) {
      case 'user':
        title = '编辑用户';
        formItems = [
          <Form.Item
            key="role"
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Input placeholder="user/admin" />
          </Form.Item>
        ];
        break;
      case 'role':
        title = '设置用户角色';
        formItems = [
          <Form.Item
            key="role"
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Input placeholder="user/admin" />
          </Form.Item>
        ];
        break;
      case 'member-level':
        title = '设置会员级别';
        formItems = [
          <Form.Item
            key="member_level"
            name="member_level"
            label="会员级别"
            rules={[{ required: true, message: '请选择会员级别' }]}
          >
            <Select placeholder="请选择会员级别">
              <Select.Option value="普通会员">普通会员</Select.Option>
              <Select.Option value="高级会员">高级会员</Select.Option>
              <Select.Option value="银卡会员">银卡会员</Select.Option>
              <Select.Option value="金卡会员">金卡会员</Select.Option>
              <Select.Option value="钻石会员">钻石会员</Select.Option>
              <Select.Option value="VIP会员">VIP会员</Select.Option>
            </Select>
          </Form.Item>
        ];
        break;
      case 'theme':
        title = editingItem ? '编辑主题' : '添加主题';
        formItems = [
          <Form.Item
            key="name"
            name="name"
            label="主题名称"
            rules={[{ required: true, message: '请输入主题名称' }]}
          >
            <Input placeholder="请输入主题名称" />
          </Form.Item>,
          <Form.Item
            key="primary_color"
            name="primary_color"
            label="主色调"
            rules={[{ required: true, message: '请输入主色调' }]}
          >
            <Input placeholder="#1890ff" />
          </Form.Item>,
          <Form.Item
            key="secondary_color"
            name="secondary_color"
            label="次色调"
          >
            <Input placeholder="#1E40AF" />
          </Form.Item>,
          <Form.Item
            key="background_color"
            name="background_color"
            label="背景色"
          >
            <Input placeholder="#F8FAFC" />
          </Form.Item>,
          <Form.Item
            key="text_color"
            name="text_color"
            label="文字色"
          >
            <Input placeholder="#1F2937" />
          </Form.Item>
        ];
        break;
      case 'code':
        title = '生成卡密';
        formItems = [
          <Form.Item
            key="type"
            name="type"
            label="卡密类型"
            rules={[{ required: true, message: '请输入卡密类型' }]}
            initialValue="premium"
          >
            <Select placeholder="请选择卡密类型">
              <Select.Option value="basic">基础会员</Select.Option>
              <Select.Option value="vip">VIP会员</Select.Option>
              <Select.Option value="premium">高级会员</Select.Option>
            </Select>
          </Form.Item>,
          <Form.Item
            key="duration_days"
            name="duration_days"
            label="有效期(天)"
            rules={[{ required: true, message: '请输入有效期' }]}
            initialValue={365}
          >
            <InputNumber 
              placeholder="365" 
              min={1} 
              max={3650}
              style={{ width: '100%' }}
            />
          </Form.Item>,
          <Form.Item
            key="count"
            name="count"
            label="生成数量"
            initialValue={1}
            rules={[{ required: true, message: '请输入生成数量' }]}
          >
            <InputNumber 
              placeholder="1" 
              min={1} 
              max={1000}
              style={{ width: '100%' }}
            />
          </Form.Item>
        ];
        break;
      case 'card-price':
        title = editingItem ? '编辑卡类定价' : '添加卡类定价';
        formItems = [
          <Form.Item
            key="card_type"
            name="card_type"
            label="卡类名称"
            rules={[{ required: true, message: '请输入卡类名称' }]}
          >
            <Input placeholder="例如: basic, premium, vip" />
          </Form.Item>,
          <Form.Item
            key="price"
            name="price"
            label="价格 (元)"
            rules={[{ required: true, message: '请输入价格' }]}
          >
            <Input type="number" step="0.01" placeholder="例如: 9.9" />
          </Form.Item>,
          <Form.Item
            key="original_price"
            name="original_price"
            label="原价 (元)"
            rules={[{ required: true, message: '请输入原价' }]}
          >
            <Input type="number" step="0.01" placeholder="例如: 19.9" />
          </Form.Item>,
          <Form.Item
            key="description"
            name="description"
            label="描述"
          >
            <Input placeholder="卡类描述" />
          </Form.Item>,
          <Form.Item
            key="is_active"
            name="is_active"
            label="是否启用"
            initialValue={editingItem ? editingItem.is_active : 1}
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        ];
        break;
    }

    return (
      <Modal
        title={title}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingItem(null);
          form.resetFields();
        }}
        onOk={handleSubmit}
      >
        <Form form={form} layout="vertical">
          {formItems}
        </Form>
      </Modal>
    );
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        padding: '0 16px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderRadius: '0 0 16px 16px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        flexWrap: 'wrap',
        minHeight: '64px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBackToApp}
            style={{ marginRight: '16px', marginBottom: '8px' }}
            size="small"
          >
            返回应用
          </Button>
          <h2 style={{ margin: 0, color: '#fff', fontSize: '18px', marginBottom: '8px' }}>管理后台</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ marginRight: '16px', color: '#fff', fontSize: '14px', marginBottom: '8px' }}>欢迎，{user?.username}</span>
          <Button onClick={logout} size="small" style={{ marginBottom: '8px' }}>退出</Button>
        </div>
      </Header>
      
      {/* 顶部导航菜单 */}
      <div style={{ 
        background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        margin: '16px',
        padding: '8px 16px'
      }}>
        <Menu
          mode="horizontal"
          selectedKeys={[selectedMenu]}
          style={{ 
            border: 'none',
            background: 'transparent',
            lineHeight: '40px'
          }}
          onClick={({ key }) => handleMenuClick(key)}
        >
          <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
            仪表盘
          </Menu.Item>
          <Menu.Item key="ts-agent" icon={<TeamOutlined />}>
            TS代理平台
          </Menu.Item>
          <Menu.Item key="users" icon={<UserOutlined />}>
            用户管理
          </Menu.Item>
          <Menu.Item key="admins" icon={<CrownOutlined />}>
            管理员管理
          </Menu.Item>
          <Menu.Item key="settings" icon={<SettingOutlined />}>
            系统设置
          </Menu.Item>
          <Menu.Item key="themes" icon={<BgColorsOutlined />}>
            主题管理
          </Menu.Item>
          <Menu.Item key="activation-codes" icon={<KeyOutlined />}>
            卡密管理
          </Menu.Item>
          <Menu.Item key="card-prices" icon={<DollarOutlined />}>
            卡类定价管理
          </Menu.Item>
          <Menu.Item key="database-backup" icon={<DatabaseOutlined />}>
            数据库备份
          </Menu.Item>
        </Menu>
      </div>
      
      <Layout style={{ padding: '0 16px 16px 16px' }}>
        <Content style={{ 
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', 
          padding: '24px', 
          margin: 0, 
          minHeight: 280,
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          {selectedMenu === 'dashboard' && renderDashboard()}
          {selectedMenu === 'users' && renderUsers()}
          {selectedMenu === 'admins' && <AdminManagePanel />}
          {selectedMenu === 'settings' && <SystemSettingsPanel />}
          {selectedMenu === 'themes' && renderThemes()}
          {selectedMenu === 'activation-codes' && renderActivationCodes()}
          {selectedMenu === 'card-prices' && renderCardPrices()}
          {selectedMenu === 'ts-agent' && <TsAgentAdminPanel />}
          {selectedMenu === 'database-backup' && renderDatabaseBackup()}
        </Content>
      </Layout>
      
      {renderModal()}
      
      {/* 查看推荐Modal */}
      <Modal
        title={`${selectedUser?.nickname || selectedUser?.phone} - 推荐记录`}
        open={referralModalVisible}
        onCancel={() => setReferralModalVisible(false)}
        width={800}
        footer={[
          <Button key="share" type="primary" onClick={handleShareReferrals}>
            分享记录
          </Button>,
          <Button key="close" onClick={() => setReferralModalVisible(false)}>
            关闭
          </Button>
        ]}
      >
        {referralData && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={5}>
                <Statistic title="总推荐人数" value={referralData.totalReferrals} />
              </Col>
              <Col span={5}>
                <Statistic title="今日推荐" value={referralData.todayReferrals} />
              </Col>
              <Col span={5}>
                <Statistic title="高级会员" value={referralData.premiumReferrals} />
              </Col>
              <Col span={4}>
                <Statistic title="VIP推荐" value={referralData.vipReferrals} />
              </Col>
              <Col span={5}>
                <Statistic 
                  title="当前积分" 
                  value={referralData.currentPoints || 0}
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
            </Row>
            
            <Table
              dataSource={referralData.referralList}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              columns={[
                {
                  title: '用户昵称',
                  dataIndex: 'nickname',
                  key: 'nickname'
                },
                {
                  title: '手机号',
                  dataIndex: 'phone',
                  key: 'phone'
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status) => {
                    const statusMap = {
                      'registered': { text: '已注册', color: 'green' },
                      'premium': { text: '高级会员', color: 'blue' },
                      'vip': { text: 'VIP会员', color: 'gold' }
                    };
                    const config = statusMap[status] || { text: status, color: 'default' };
                    return <Tag color={config.color}>{config.text}</Tag>;
                  }
                },
                {
                  title: '注册时间',
                  dataIndex: 'joinDate',
                  key: 'joinDate'
                },
                {
                  title: '奖励积分',
                  dataIndex: 'pointsAwarded',
                  key: 'pointsAwarded',
                  render: (points) => <span style={{ color: '#52c41a' }}>+{points}</span>
                }
              ]}
            />
          </div>
        )}
      </Modal>
      
      {/* 积分管理Modal */}
      <Modal
        title={`${selectedUser?.nickname || selectedUser?.phone} - 积分管理`}
        open={pointsModalVisible}
        onCancel={() => setPointsModalVisible(false)}
        width={800}
        footer={[
          <Button key="share" onClick={handleSharePoints}>
            分享记录
          </Button>,
          <Button key="close" onClick={() => setPointsModalVisible(false)}>
            关闭
          </Button>
        ]}
      >
        {pointsData && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic title="总积分" value={pointsData.totalPoints} />
              </Col>
              <Col span={8}>
                <Statistic title="已用积分" value={pointsData.usedPoints} />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="剩余积分" 
                  value={pointsData.remainingPoints}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
            </Row>
            
            <Card title="修改已用积分" style={{ marginBottom: 24 }}>
              <Form form={pointsForm} onFinish={handleUpdatePoints} layout="inline">
                <Form.Item
                  name="used_points"
                  label="已用积分"
                  rules={[
                    { required: true, message: '请输入已用积分' },
                    { type: 'number', min: 0, message: '积分不能为负数' }
                  ]}
                >
                  <InputNumber min={0} style={{ width: 200 }} />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit">
                    更新
                  </Button>
                </Form.Item>
              </Form>
              <div style={{ marginTop: 12, color: '#666', fontSize: 12 }}>
                剩余积分 = 总积分 - 已用积分
              </div>
            </Card>
            
            <Card title="积分记录">
              <Table
                dataSource={pointsData.recentLogs}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                columns={[
                  {
                    title: '时间',
                    dataIndex: 'createdAt',
                    key: 'createdAt',
                    width: 180
                  },
                  {
                    title: '说明',
                    dataIndex: 'description',
                    key: 'description'
                  },
                  {
                    title: '积分变动',
                    dataIndex: 'points',
                    key: 'points',
                    render: (points) => (
                      <span style={{ color: points > 0 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
                        {points > 0 ? '+' : ''}{points}
                      </span>
                    )
                  }
                ]}
              />
            </Card>
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default Admin;
